
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Moon, Sun } from 'lucide-react';
import {
  ORBITAL_ICON_BOX_STYLE,
  POWER_INFO_BADGE_CLASS,
} from '@/components/help-center/latest-power-shared';
import { normalizeSmileyCode, normalizeSmileySizePx, smilieOrPawnImageCandidates } from '@/lib/xat-smilie';
import { cn } from '@/lib/utils';
import { MaskedSvgIcon } from '@/components/help-center/MaskedSvgIcon';

export type PowerGalleryViewMode = 'dark' | 'light';

const STORAGE_KEY = 'xat-power-gallery-view';

export const GALLERY_BOX_STYLE: Record<PowerGalleryViewMode, CSSProperties> = {
  dark: ORBITAL_ICON_BOX_STYLE,
  light: {
    background: '#ffffff',
    boxShadow: '0 0 0 1px #e5e7eb',
  },
};

export function galleryBadgeClass(mode: PowerGalleryViewMode, copied?: boolean) {
  return cn(
    'relative z-[1] flex w-full min-w-0 items-center justify-center overflow-hidden px-2 py-1.5 transition-colors',
    mode === 'light'
      ? cn(
          'rounded-2xl border border-gray-200 bg-gray-50',
          copied && 'border-[rgba(0,11,106,0.28)] bg-[rgba(0,11,106,0.06)]'
        )
      : cn(POWER_INFO_BADGE_CLASS, copied && 'border-[rgba(0,11,106,0.45)] from-white/[0.10]')
  );
}

export function galleryCodeTextClass(mode: PowerGalleryViewMode) {
  return mode === 'light' ? 'text-gray-800' : 'text-blue-50';
}

export function usePowerGalleryViewMode() {
  const ctx = useContext(PowerGalleryViewContext);
  if (ctx) return ctx;
  return usePowerGalleryViewModeState();
}

function usePowerGalleryViewModeState() {
  const [mode, setModeState] = useState<PowerGalleryViewMode>('light');

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark') {
        setModeState(stored);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const setMode = (next: PowerGalleryViewMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  return { mode, setMode };
}

const PowerGalleryViewContext = createContext<ReturnType<typeof usePowerGalleryViewModeState> | null>(
  null
);

export function PowerGalleryViewProvider({ children }: { children: ReactNode }) {
  const value = usePowerGalleryViewModeState();
  return (
    <PowerGalleryViewContext.Provider value={value}>{children}</PowerGalleryViewContext.Provider>
  );
}

type PowerGalleryViewToggleProps = {
  mode: PowerGalleryViewMode;
  onChange: (mode: PowerGalleryViewMode) => void;
  className?: string;
};

const SECTION_SMILEY_IMG_CLASS: Record<string, string> = {
  hat: 'h-[30px] w-[30px] translate-y-[4px] object-contain object-bottom',
  smile: 'h-8 w-8 object-contain object-center',
};

const HINT_SMILEY_IMG_CLASS: Record<string, string> = {
  hat: 'h-[22px] w-[22px] translate-y-[2px] object-contain object-bottom',
  smile: 'h-5 w-5 object-contain object-center',
};

const SECTION_SMILEY_FETCH_PX = 40;

function SectionSmileyImg({
  code,
  fetchSizePx = SECTION_SMILEY_FETCH_PX,
  className,
}: {
  code: string;
  fetchSizePx?: number;
  className?: string;
}) {
  const key = normalizeSmileyCode(code);
  const candidates = useMemo(() => {
    const preferred = normalizeSmileySizePx(fetchSizePx);
    // Tenta vários tamanhos oficiais — alguns powers só existem em 40/80.
    const sizes = Array.from(
      new Set([preferred, 80, 40, 160, 20] as const)
    );
    const urls: string[] = [];
    for (const size of sizes) {
      urls.push(...smilieOrPawnImageCandidates(key, size));
    }
    return Array.from(new Set(urls));
  }, [key, fetchSizePx]);
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [key, fetchSizePx]);

  if (!key || candidateIndex >= candidates.length) {
    return (
      <span
        className={cn(
          'flex items-center justify-center font-mono text-[10px] text-white/50',
          className
        )}
        aria-hidden
      >
        {key || '?'}
      </span>
    );
  }

  return (
    <img
      src={candidates[candidateIndex]}
      alt=""
      className={className}
      draggable={false}
      aria-hidden
      loading="lazy"
      decoding="async"
      onError={() => setCandidateIndex((index) => index + 1)}
    />
  );
}

export function GalleryCountBadge({
  count,
  tone,
}: {
  count: number;
  tone: 'violet' | 'amber';
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[1.75rem] items-center justify-center rounded-full px-2.5 py-0.5 font-poppins text-xs font-bold tabular-nums ring-1 ring-inset',
        tone === 'violet'
          ? 'bg-violet-100 text-violet-700 ring-violet-200'
          : 'bg-amber-100 text-amber-800 ring-amber-200'
      )}
    >
      {count}
    </span>
  );
}

export function GallerySectionSmileyIcon({
  code,
  bgClass,
  variant = 'header',
}: {
  code: string;
  bgClass: string;
  variant?: 'header' | 'hint';
}) {
  const key = normalizeSmileyCode(code);
  const isHint = variant === 'hint';
  const imgClass =
    (isHint ? HINT_SMILEY_IMG_CLASS[key] : SECTION_SMILEY_IMG_CLASS[key]) ??
    (isHint ? 'h-5 w-5 object-contain object-center' : 'h-8 w-8 object-contain object-center');

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-xl',
        isHint ? 'h-7 w-7' : 'h-9 w-9',
        key === 'hat' && 'items-end pb-0.5',
        bgClass
      )}
    >
      <SectionSmileyImg code={key} className={imgClass} />
    </div>
  );
}

const POWER_SECTION_SMILEY_SIZE = {
  md: { img: 'h-9 w-9', api: 40 },
  lg: { img: 'h-11 w-11', api: 40 },
  xl: { img: 'h-14 w-14', api: 80 },
  /** Destaque da secção Lore / história — só tamanhos oficiais do CDN (20/40/80/160). */
  hero: { img: 'h-[4.5rem] w-[4.5rem] sm:h-20 sm:w-20', api: 80 },
} as const;

/** Smiley de secção sem fundo colorido (títulos de página de power). */
export function PowerSectionSmileyIcon({
  code,
  size = 'md',
}: {
  code: string;
  size?: keyof typeof POWER_SECTION_SMILEY_SIZE;
}) {
  const key = normalizeSmileyCode(code);
  const spec = POWER_SECTION_SMILEY_SIZE[size];
  const imgClass =
    (size === 'md' ? SECTION_SMILEY_IMG_CLASS[key] : undefined) ??
    `${spec.img} object-contain object-center`;

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center',
        key === 'hat' && 'items-end pb-0.5'
      )}
    >
      <SectionSmileyImg code={key} fetchSizePx={spec.api} className={imgClass} />
    </div>
  );
}

/** Ícone pequeno para badges de contagem (smilies) — smiley CDN original. */
export function SmileyBadgeIcon({ code = 'smile', className }: { code?: string; className?: string }) {
  const key = normalizeSmileyCode(code);
  return (
    <SectionSmileyImg
      code={key}
      fetchSizePx={20}
      className={cn('h-5 w-5 shrink-0 object-contain', className)}
    />
  );
}

/** Ícone SVG estático (`/icons/smile.svg`) para badges em fundo claro. */
export function SmileySvgBadgeIcon({ className }: { className?: string }) {
  return (
    <MaskedSvgIcon
      src="/icons/smile.svg"
      className={cn('h-5 w-5 shrink-0', className)}
    />
  );
}

/** Ícone pequeno para badges de contagem (peões) — usa /icons/pawn.svg. */
export function PawnBadgeIcon({ className }: { className?: string }) {
  return (
    <MaskedSvgIcon
      src="/icons/pawn.svg"
      className={cn('h-5 w-5 shrink-0', className)}
    />
  );
}

export function PowerGalleryViewToggle({ mode, onChange, className }: PowerGalleryViewToggleProps) {
  return (
    <div
      className={cn(
        'inline-flex rounded-xl border border-gray-200 bg-gray-50/80 p-1 shadow-sm',
        className
      )}
      role="group"
      aria-label="Modo de visualização da galeria"
    >
      <button
        type="button"
        aria-pressed={mode === 'dark'}
        onClick={() => onChange('dark')}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-poppins text-xs font-semibold transition-all',
          mode === 'dark'
            ? 'bg-[#0a1450] text-white shadow-sm'
            : 'text-gray-500 hover:text-gray-800'
        )}
      >
        <Moon className="h-3.5 w-3.5" aria-hidden />
        Escuro
      </button>
      <button
        type="button"
        aria-pressed={mode === 'light'}
        onClick={() => onChange('light')}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-poppins text-xs font-semibold transition-all',
          mode === 'light'
            ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
            : 'text-gray-500 hover:text-gray-800'
        )}
      >
        <Sun className="h-3.5 w-3.5" aria-hidden />
        Claro
      </button>
    </div>
  );
}
